// Supabase Storage API for profile images
import supabase from "../config/supabase";

// Must match the bucket's actual ID in Supabase Storage exactly (it was
// created as "Profile Images" — capitalized, with a space — not the
// lowercase-hyphenated slug you'd expect). A mismatch here fails uploads
// silently with a "bucket not found" error, which is why photos previously
// never persisted.
const BUCKET_NAME = 'Profile Images';

/**
 * Upload a profile image to Supabase Storage
 * @param {string} userId - User ID for the file path
 * @param {Blob} imageBlob - Image blob to upload
 * @param {string} fileName - Original file name (optional)
 * @returns {Promise<{success: boolean, url?: string, error?: string}>}
 */
export const uploadProfileImage = async (userId, imageBlob, fileName = 'profile.jpg') => {
  try {
    if (!userId) {
      return { success: false, error: 'User ID is required' };
    }

    // Create unique file path: userId/timestamp-filename
    const timestamp = Date.now();
    const ext = fileName.split('.').pop() || 'jpg';
    const filePath = `${userId}/${timestamp}.${ext}`;

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from(BUCKET_NAME)
      .upload(filePath, imageBlob, {
        contentType: 'image/jpeg',
        upsert: false,
      });

    if (error) {
      console.error('Supabase storage upload error:', error);
      return { success: false, error: error.message };
    }

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);

    return { success: true, url: publicUrl, path: filePath };
  } catch (err) {
    console.error('Upload profile image error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete a profile image from Supabase Storage
 * @param {string} filePath - Full file path in storage
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteProfileImage = async (filePath) => {
  try {
    if (!filePath) {
      return { success: false, error: 'File path is required' };
    }

    const { error } = await supabase.storage
      .from(BUCKET_NAME)
      .remove([filePath]);

    if (error) {
      console.error('Supabase storage delete error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Delete profile image error:', err);
    return { success: false, error: err.message };
  }
};

/**
 * Delete old profile images for a user (keep only the latest)
 * @param {string} userId - User ID
 * @param {string} keepFilePath - File path to keep (optional)
 */
export const cleanupOldProfileImages = async (userId, keepFilePath = null) => {
  try {
    // List all files for this user
    const { data: files, error } = await supabase.storage
      .from(BUCKET_NAME)
      .list(userId);

    if (error || !files || files.length === 0) {
      return { success: true }; // Nothing to clean up
    }

    // Filter out the file we want to keep
    const filesToDelete = files
      .filter(file => !keepFilePath || !keepFilePath.includes(file.name))
      .map(file => `${userId}/${file.name}`);

    if (filesToDelete.length > 0) {
      await supabase.storage
        .from(BUCKET_NAME)
        .remove(filesToDelete);
    }

    return { success: true };
  } catch (err) {
    console.error('Cleanup old profile images error:', err);
    return { success: false, error: err.message };
  }
};

export const storageApi = {
  uploadProfileImage,
  deleteProfileImage,
  cleanupOldProfileImages,
};
